import Loading from "../../core/blocks/Loading";
import { useDates } from "../../../dates/DatesContext";
import { useFetch } from "../../../hooks/api";
import { Fragment, useMemo, useState } from "react";
import { Row, Col, Nav, Badge } from "react-bootstrap";
import { Card } from "react-bootstrap/esm";
import getMetricSpec from "../../../modules/merchandising/utils/metrics";
import ProductImage from "../category/listings/ProductImage";
import Metric from "../../core/blocks/SummaryMetric";
import ReactTable from "react-table-6";
import ReactTablePagination from "../growth/ProductsCVTablePagination";
import { makeColumn, col } from "./columns";
import useColumnSelector from "../../../hooks/useColumnSelector";
import { Link } from "@tanstack/react-router";
import MultiMetricChart from "./MultiMetricChart";
import useSelectedMetrics from "./hooks/useSelectedMetrics";
import { INVENTORY_STATES, SALE_STATES } from "../../core/blocks/AdPlan/constants";
import moment from "moment";
import checkApplication from "../../../utils/checkApplication";
import { ADVERTISING, CONTENT, DEALS, DYNAMIC_PRICING } from "./constants";
import generateLink from "../../../utils/generateLink";
import ListingQuality from "../../../modules/product_quality/components/ListingQuality";
import { getBadgeVariant } from "../../../modules/product_quality/utils";
import ConversionLegend from "../../../modules/product_quality/components/ConversionLegend";
import { useQuery } from "@tanstack/react-query";
import { useProductByDate } from "../../../modules/perf_reports/api/product/useProductByDate";
import { useProductTotals } from "../../../modules/perf_reports/api/product/useProductTotals";
import { HoverInfo } from "../../core/basic/Tooltip";
import { useAppSelector } from "../../../redux/hooks";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../../../i18n/useDateFormatter";

const ContentSection = ({ productId, fullPageLayout = false }) => {
  const { t } = useTranslation(["merchandising"]);
  const { loading, data } = useFetch(
    ["productQuality", productId],
    `/merchandise/quality/${productId}`,
    {},
    {
      select: (data) => data?.data,
    },
  );
  const metrics = useMemo(
    () => [
      getMetricSpec("seo_score"),
      getMetricSpec("buyability_score"),
      getMetricSpec("conversion_rate"),
      getMetricSpec("ctr"),
    ],
    [],
  );
  const terms = useMemo(() => {
    const seoKeywords = data?.seo_keywords?.keywords;
    const seoSearchTerms = data?.seo_keywords?.search_terms;
    if (seoKeywords && seoSearchTerms) {
      [seoKeywords, seoSearchTerms].forEach((data) => {
        for (let i = 0; i < data.length; i++) {
          const kw = data[i]["keyword_text"];
          const kwData = data[i];
          kwData.cr = 0;
          kwData.ctr = 0;
          kwData.acos = 0;

          if (kw.length <= 1) {
            continue;
          }

          if (kwData.clicks > 0) {
            kwData.cr = kwData.conversions / kwData.clicks;
          }
          if (kwData.impressions > 0) {
            kwData.ctr = kwData.clicks / kwData.impressions;
          }
          if (kwData.sales > 0) {
            kwData.acos = kwData.cost / kwData.sales;
          }

          if (!kwData.cr) {
            kwData.cr = 0;
          }
        }
      });

      seoKeywords.sort(function (a, b) {
        return b.cr - a.cr;
      });

      seoSearchTerms.sort(function (a, b) {
        return b.cr - a.cr;
      });
      return {
        seoKeywords,
        seoSearchTerms,
      };
    }
    return {};
  }, [data]);

  if (loading) {
    return <Loading height={"300px"} fullPage={false} />;
  }
  return (
    <>
      <Row>
        <Col xs={3} className="pt-5 ps-4">
          <Row>
            <Col xs={12}>{data?.content_details && <ListingQuality contentDetails={data?.content_details} />}</Col>
          </Row>
        </Col>
        <Col xs={5} className="pt-5">
          <Row>
            <Col xs={12}>
              <div className="fs-4 pb-2" style={{ fontWeight: "bold" }}>
                {t(($) => $.merchandising["Top Converting Keywords"])}{" "}
                <HoverInfo iconCss={{ color: "grey", display: "inline" }}>
                  <>
                    {t(($) => $.merchandising["The top converting keywords for this product."])}
                    <br />
                    <ConversionLegend xs={6} />
                  </>
                </HoverInfo>
              </div>
              <div
                style={{
                  maxHeight: "600px",
                  overflow: "hidden",
                  overflowY: "scroll",
                  paddingTop: "2px",
                  paddingBottom: "10px",
                }}
              >
                {terms?.seoKeywords &&
                  terms?.seoKeywords?.slice(0, 50)?.map((k) => (
                    <Badge key={k.keyword_text} bg={getBadgeVariant(k)} className="mx-1 my-1 fs-5 badge-keyword">
                      {k.keyword_text}
                    </Badge>
                  ))}
              </div>
            </Col>
          </Row>
        </Col>
        <Col xs={4} className="pt-5">
          <Row>
            {metrics.map((m) => (
              <Col key={m.id} xs={6} style={{ marginBottom: "3rem" }}>
                <Metric metric={m} data={data} />
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
      <Row>
        <Col xs={12} className={`text-center ${fullPageLayout ? "fs-standard" : ""}`}>
          <Link to="/user/content/seo" search={{ id: String(productId) }}>
            {t(($) => $.merchandising["View Product Content"])}
          </Link>
        </Col>
      </Row>
    </>
  );
};

const DealsSection = ({ productId, marketPlace, categoryId }) => {
  const { t } = useTranslation(["merchandising"]);
  const { selectedMetrics, toggleMetric } = useSelectedMetrics(["promo_sales"]);
  const { date, start, end, preStart, preEnd, group } = useDates();
  const shortDate = useDateFormatter("short");

  const filters = {
    market_place: marketPlace.marketPlace,
    group_by: group,
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
    pre_start: preStart.format("YYYY-MM-DD"),
    pre_end: preEnd.format("YYYY-MM-DD"),
    product_id: productId,
  };
  const { data: productDealStats, isLoading: isProductDealStatsLoading } = useFetch(
    ["productDealStats", productId, marketPlace, group, start, end, preStart, preEnd],
    "/merchandise/promos_report/",
    filters,
    {
      select: (data) => data?.data,
    },
  );

  const metrics = useMemo(
    () => [
      getMetricSpec("promo_sales"),
      getMetricSpec("promo_units"),
      getMetricSpec("rods"),
      getMetricSpec("promo_cost"),
    ],
    [],
  );

  const columnSpecs = useMemo(() => {
    return [
      col(
        t(($) => $.merchandising["Promotion"]),
        "promotion",
        "promotion_report_product.promotion_report.promotion_name",
      ),
      col(
        t(($) => $.merchandising["Status"]),
        "text",
        "status",
      ),
      col(
        t(($) => $.merchandising["Type"]),
        "text",
        "promotionType",
      ),
      col(
        t(($) => $.merchandising["Start"]),
        "date",
        "promotion_report_product.promotion_report.start_datetime",
      ),
      col(
        t(($) => $.merchandising["Deal Price"]),
        "currency",
        "deal_price",
      ),
      col(
        t(($) => $.merchandising["End"]),
        "date",
        "promotion_report_product.promotion_report.end_datetime",
      ),
      col(
        t(($) => $.merchandising["Promo Sales"]),
        "currency",
        "product_revenue",
        {
          compareKey: "sales_est",
          compareType: "est.",
        },
      ),
      col(
        t(($) => $.merchandising["Promo Units"]),
        "number",
        "product_units_sold",
        {
          compareKey: "units_est",
          compareType: "est.",
        },
      ),
      col(
        t(($) => $.merchandising["Total Discounts"]),
        "currency",
        "cost",
      ),
      col(
        t(($) => $.merchandising["Glance Views"]),
        "number",
        "product_glance_views",
      ),
      col(
        t(($) => $.merchandising["Conversion Rate"]),
        "percent",
        "conversion",
      ),
    ];
  }, [t]);

  const promos = useMemo(() => {
    return (
      productDealStats?.promos?.map((p) => {
        const promotionType =
          {
            BEST_DEAL: t(($) => $.merchandising["Best Deal"]),
            LIGHTNING_DEAL: t(($) => $.merchandising["Lightning Deal"]),
          }?.[p?.promotion_report_product?.promotion_report?.promotion_type] ?? t(($) => $.merchandising["Unknown"]);
        let status = p?.promotion_report_product?.promotion_report?.status;
        const start = p?.promotion_report_product?.promotion_report?.start_datetime;
        const end = p?.promotion_report_product?.promotion_report?.end_datetime;
        if (status === "APPROVED") {
          if (moment(start).isBefore(moment()) && moment(end).isAfter(moment())) {
            status = t(($) => $.merchandising["In Progress"]);
          } else if (moment(end).isBefore(moment())) {
            status = t(($) => $.merchandising["Completed"]);
          } else {
            status = t(($) => $.merchandising["Approved"]);
          }
        } else {
          status = status === "CANCELED" ? t(($) => $.merchandising["Canceled"]) : status?.toLowerCase;
        }
        return {
          ...p,
          status,
          promotionType,
        };
      }) ?? []
    );
  }, [productDealStats, t]);

  const createColumn = makeColumn(productDealStats, marketPlace, false, date, false, categoryId, shortDate);
  const [columns] = useColumnSelector(
    () => columnSpecs.map((c) => createColumn(c.header, c.key, c.type, c.options)),
    [columnSpecs],
  );
  const columnOrder = columns.map((col) => col.id);

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  if (isProductDealStatsLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <>
      <Row>
        <Col xs={8}>
          {productDealStats?.dates && (
            <MultiMetricChart
              metrics={metrics}
              selectedMetrics={selectedMetrics}
              data={productDealStats.dates}
              group={group}
              handleLegendChange={handleLegendChange}
            />
          )}
        </Col>
        <Col xs={4} className="pt-5">
          <Row>
            {metrics.map((m) => (
              <Col key={m.id} xs={6} style={{ marginBottom: "3rem" }} onClick={() => toggleMetric(m.id)}>
                <Metric
                  metric={m}
                  data={productDealStats?.total}
                  showTarget
                  selected={selectedMetrics.includes(m.id)}
                />
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          {promos?.length > 0 && (
            <ReactTable
              showPaginationBottom={false}
              showPaginationTop
              PaginationComponent={ReactTablePagination}
              columns={columns}
              data={promos ?? []}
              defaultPageSize={promos?.length ?? 0}
              className="-highlight"
              filterable={false}
              state={columnOrder}
              getPaginationProps={() => ({
                hideSearch: true,
                hideFilters: true,
                table: "plan-products",
                style: { marginBottom: "2rem" },
                carouselLayout: false,
                disableSearchFocus: true,
              })}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

const AdvertisingSection = ({ productId, data, productDetails, fullPageLayout = false, channel, categoryId }) => {
  const { t } = useTranslation(["merchandising"]);
  const { selectedMetrics, toggleMetric } = useSelectedMetrics(["ad_sales"]);
  const { date, start, end, preStart, preEnd, group } = useDates();
  const shortDate = useDateFormatter("short");
  const marketPlace = useAppSelector((state) => state.marketPlace);

  const filters = {
    channel,
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    product_id: productId,
  };

  const { data: productAdStats, isLoading: isProductAdDataLoading } = useFetch(
    ["productAdStats", productId, marketPlace.marketPlace, group, start, end, preStart, preEnd],
    "/api/data_report/product_ad_report",
    filters,
    {
      select: (data) => data?.data,
    },
  );

  const metrics = useMemo(
    () => [getMetricSpec("ad_sales"), getMetricSpec("ad_spend"), getMetricSpec("clicks"), getMetricSpec("roas")],
    [],
  );

  const columnSpecs = useMemo(() => {
    return [
      col(
        t(($) => $.merchandising["Media Plan"]),
        "advertisement",
        "advertisement_name",
        {
          className: "text-start",
          link: {
            type: "advertisement",
            handleClick: null,
            productIdentifier: null,
            styles: "inherit",
            openInNewTab: false,
            customText: null,
            textAccessor: "advertisement_name",
          },
        },
      ),
      col(
        t(($) => $.merchandising["Ad Sales"]),
        "currency",
        "ad_sales",
      ),
      col(
        t(($) => $.merchandising["Ad Spend"]),
        "currency",
        "cost",
        { noColor: true },
      ),
      col(
        t(($) => $.merchandising["Ad Units"]),
        "number",
        "conversion",
        { noColor: true },
      ),
      col(
        t(($) => $.merchandising["Clicks"]),
        "number",
        "clicks",
        { noColor: true },
      ),
      col(
        t(($) => $.merchandising["ROAS"]),
        "number",
        "roas",
      ),
    ];
  }, [t]);

  const createColumn = makeColumn(productAdStats, marketPlace, false, date, false, categoryId, shortDate);
  const [columns] = useColumnSelector(
    () => columnSpecs.map((c) => createColumn(c.header, c.key, c.type, c.options)),
    [columnSpecs],
  );
  const columnOrder = columns.map((col) => col.id);

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  if (isProductAdDataLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <>
      <Row>
        <Col xs={8}>
          {data?.date && (
            <MultiMetricChart
              metrics={metrics}
              selectedMetrics={selectedMetrics}
              data={data.date}
              group={group}
              handleLegendChange={handleLegendChange}
            />
          )}
        </Col>
        <Col xs={4} className="pt-5">
          <Row>
            {metrics.map((m) => (
              <Col key={m.id} xs={6} style={{ marginBottom: "3rem" }} onClick={() => toggleMetric(m.id)}>
                <Metric metric={m} data={data?.total} showTarget selected={selectedMetrics.includes(m.id)} />
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          {productAdStats?.product_ads?.length > 0 && (
            <ReactTable
              showPaginationBottom={false}
              showPaginationTop
              PaginationComponent={ReactTablePagination}
              columns={columns}
              data={productAdStats?.product_ads ?? []}
              defaultPageSize={productAdStats?.product_ads?.length ?? 0}
              className="-highlight"
              filterable={false}
              state={columnOrder}
              getPaginationProps={() => ({
                hideSearch: true,
                hideFilters: true,
                table: "plan-products",
                style: { marginBottom: "2rem" },
                carouselLayout: false,
                disableSearchFocus: true,
              })}
            />
          )}
        </Col>
      </Row>
      {!fullPageLayout && (
        <Row>
          <Col xs={12} className="text-center">
            {generateLink(
              "listing",
              productDetails,
              null,
              "both",
              "inherit",
              false,
              t(($) => $.merchandising["View Listing"]),
            )}
          </Col>
        </Row>
      )}
    </>
  );
};

const OverviewSection = ({ data, isLoading }) => {
  const { selectedMetrics, toggleMetric } = useSelectedMetrics(["sales"]);
  const { group } = useDates();

  const metrics = useMemo(
    () => [getMetricSpec("sales"), getMetricSpec("page_views"), getMetricSpec("units"), getMetricSpec("margin")],
    [],
  );

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  if (isLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <Row>
      <Col xs={8}>
        {data?.date && (
          <MultiMetricChart
            metrics={metrics}
            selectedMetrics={selectedMetrics}
            data={data.date}
            group={group}
            handleLegendChange={handleLegendChange}
          />
        )}
      </Col>
      <Col xs={4} className="pt-5">
        <Row>
          {metrics.map((m) => (
            <Col key={m.id} xs={6} style={{ marginBottom: "3rem" }} onClick={() => toggleMetric(m.id)}>
              <Metric
                metric={m}
                data={data?.total}
                showTarget
                legendColor={m.color}
                selected={selectedMetrics.includes(m.id)}
              />
            </Col>
          ))}
        </Row>
      </Col>
    </Row>
  );
};

const PricingSection = ({ productId, data, isLoading }) => {
  const { t } = useTranslation(["merchandising"]);
  const { selectedMetrics, toggleMetric } = useSelectedMetrics(["profit"]);
  const { group } = useDates();
  const { data: pricingProduct, isLoading: isPricingProductDataLoading } = useFetch(
    ["product_pricing", productId],
    `/pricing/products/${productId}/`,
    {},
    {
      select: (d) => d?.data,
    },
  );

  const metrics = useMemo(
    () => [getMetricSpec("profit"), getMetricSpec("price"), getMetricSpec("units"), getMetricSpec("bsr")],
    [],
  );

  const { confidence, enroll_date, sales_volume_control_slider } = pricingProduct ?? {};

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  if (isLoading || isPricingProductDataLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <>
      {(confidence || enroll_date) && (
        <Row className="pt-4">
          <Col xs={6} md={{ span: 2, offset: 6 }} className="d-flex align-items-center justify-content-end">
            <>
              <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 pe-3">
                {t(($) => $.merchandising["Strategy"])}
              </h6>
              <Badge
                className={`badge bg-trellis-${enroll_date ? "purple" : "ligthgrey"} text-end`}
                style={{
                  borderRadius: "5px",
                }}
              >
                {enroll_date && sales_volume_control_slider >= 0 && sales_volume_control_slider <= 0 && (
                  <span>{t(($) => $.merchandising["Balanced"])}</span>
                )}
                {enroll_date && sales_volume_control_slider < 0 && <span>{t(($) => $.merchandising["Volume"])}</span>}
                {enroll_date && sales_volume_control_slider > 0 && <span>{t(($) => $.merchandising["Margin"])}</span>}
                {!enroll_date && <span>{t(($) => $.merchandising["n/a"])}</span>}
              </Badge>
            </>
          </Col>
          <Col xs={6} md={{ span: 2 }} className="d-flex align-items-center justify-content-end">
            <>
              <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 pe-3">
                {t(($) => $.merchandising["Status"])}
              </h6>
              <Badge
                className={`badge bg-trellis-${enroll_date ? "green" : "ligthgrey"} text-end`}
                style={{
                  borderRadius: "5px",
                }}
              >
                {enroll_date && <span>{t(($) => $.merchandising.pricingStatus["Enrolled"])}</span>}
                {!enroll_date && <span>{t(($) => $.merchandising.pricingStatus["Not Enrolled"])}</span>}
              </Badge>
            </>
          </Col>
        </Row>
      )}
      <Row>
        <Col xs={8}>
          <MultiMetricChart
            metrics={metrics}
            selectedMetrics={selectedMetrics}
            data={data.date}
            group={group}
            handleLegendChange={handleLegendChange}
          />
        </Col>
        <Col xs={4} className="pt-5">
          <Row>
            {metrics.map((m) => (
              <Col
                key={m.id}
                xs={6}
                style={{ marginBottom: "3rem" }}
                onClick={() => {
                  toggleMetric(m.id);
                }}
              >
                <Metric metric={m} data={data?.total} showTarget selected={selectedMetrics.includes(m.id)} />
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </>
  );
};

const ProductCard = ({
  productId,
  marketPlace,
  channel,
  user,
  additionalTabs,
  initialTab,
  hideTabNav = false,
  hasBorder = true,
}) => {
  const { t } = useTranslation(["merchandising", "advertising"]);
  const { start, end, preStart, preEnd, group } = useDates();

  const { isLoading, data: product } = useFetch(
    ["products", productId, group, start, end, preStart, preEnd],
    `/api/gvads/productid/${productId}`,
    {},
    { select: (res) => res.data },
  );

  const byDateQuery = useQuery(useProductByDate({ productId }));

  const totalsQuery = useQuery(useProductTotals({ productId }));

  const isOverviewDataLoading = byDateQuery.isLoading || totalsQuery.isLoading;
  const data = useMemo(() => {
    if (!byDateQuery.data && !totalsQuery.data) return undefined;
    return {
      date: byDateQuery.data?.data,
      total: totalsQuery.data?.data,
    };
  }, [byDateQuery.data, totalsQuery.data]);

  const categoryId = product?.product_category;

  const salesStateArray = [SALE_STATES.BEST_SELLER, SALE_STATES.CLEARANCE, SALE_STATES.NEW_PRODUCT];
  const inventoryStateArray = [INVENTORY_STATES.OVERSTOCK, INVENTORY_STATES.LOW_STOCK, INVENTORY_STATES.OUT_OF_STOCK];
  const salesLabelSelector = salesStateArray.find((s) => s.value === product?.sales_state)?.label;
  const inventoryBadgeSelector = inventoryStateArray.find((s) => s.value === product?.stock_status)?.label;
  const [tab, setTab] = useState(initialTab ?? "overview");
  return (
    <>
      {isLoading ?
        <Loading height={"300px"} fullPage={false} />
      : <Card className="fs-standard mb-0" style={hasBorder ? {} : { borderRadius: 0, boxShadow: "none" }}>
          <Card.Body>
            <Row>
              <Col xs={12}>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "120px", paddingRight: "20px" }}>
                    <ProductImage height={100} width={100} id={productId} style={{ borderRadius: 15 }} />
                  </div>
                  <div>
                    <Row>
                      <Col xs={12}>
                        <h3
                          style={{
                            marginBottom: "0.2rem",
                            marginTop: "0",
                            fontSize: 22,
                          }}
                        >
                          {product?.product_title}
                        </h3>
                      </Col>
                      <Col xs={12}>
                        {product?.asin} ({product?.sku})
                      </Col>
                      <Col xs={12}>
                        {inventoryBadgeSelector && (
                          <Badge
                            className="trellis-badge bg-trellis-purple"
                            style={{
                              marginRight: "0.5rem",
                              backgroundColor: "#c43066",
                            }}
                            pill
                          >
                            {t(inventoryBadgeSelector)}
                          </Badge>
                        )}
                        {salesLabelSelector && (
                          <Badge className="trellis-badge bg-trellis-fuchsia" pill>
                            {t(salesLabelSelector)}
                          </Badge>
                        )}
                      </Col>
                    </Row>
                  </div>
                </div>
              </Col>
            </Row>
            {!hideTabNav && (
              <Row>
                <Col xs={12} className="mt-5 ph-4 mb-3 border-bottom">
                  <Nav
                    variant="underline"
                    onSelect={(k) => {
                      return setTab(k);
                    }}
                    activeKey={tab}
                  >
                    {additionalTabs?.map((additionalTab) => (
                      <Nav.Item key={additionalTab.title}>
                        <Nav.Link eventKey={additionalTab.title?.toLowerCase()}>{additionalTab.title}</Nav.Link>
                      </Nav.Item>
                    ))}
                    <Nav.Item>
                      <Nav.Link eventKey="overview">{t(($) => $.merchandising["Overview"])}</Nav.Link>
                    </Nav.Item>
                    {checkApplication(user, ADVERTISING) && (
                      <Nav.Item>
                        <Nav.Link eventKey="advertising">{t(($) => $.merchandising["Advertising"])}</Nav.Link>
                      </Nav.Item>
                    )}
                    {checkApplication(user, DEALS) && (
                      <Nav.Item>
                        <Nav.Link eventKey="deals">{t(($) => $.merchandising["Deals"])}</Nav.Link>
                      </Nav.Item>
                    )}
                    {checkApplication(user, DYNAMIC_PRICING) && (
                      <Nav.Item>
                        <Nav.Link eventKey="pricing">{t(($) => $.merchandising["Dynamic Pricing"])}</Nav.Link>
                      </Nav.Item>
                    )}
                    {checkApplication(user, CONTENT) && (
                      <Nav.Item>
                        <Nav.Link eventKey="content">{t(($) => $.merchandising["Content"])}</Nav.Link>
                      </Nav.Item>
                    )}
                  </Nav>
                </Col>
              </Row>
            )}
            <Row>
              <Col xs={12}>
                {additionalTabs?.map((additionalTab) => (
                  <Fragment key={additionalTab.title}>
                    {tab === additionalTab.title?.toLowerCase() && additionalTab.content}
                  </Fragment>
                ))}
                {tab === "overview" && (
                  <OverviewSection channel={channel} data={data} isLoading={isOverviewDataLoading} />
                )}
                {tab === "advertising" && (
                  <AdvertisingSection
                    channel={channel}
                    productId={productId}
                    data={data}
                    productDetails={product}
                    categoryId={categoryId}
                  />
                )}
                {tab === "deals" && (
                  <DealsSection
                    channel={channel}
                    productId={productId}
                    marketPlace={marketPlace.marketPlace}
                    categoryId={categoryId}
                  />
                )}
                {tab === "pricing" && (
                  <PricingSection
                    channel={channel}
                    productId={productId}
                    data={data}
                    isLoading={isOverviewDataLoading}
                  />
                )}
                {tab === "content" && <ContentSection productId={productId} />}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      }
    </>
  );
};

export default ProductCard;
